import React, { useCallback, useEffect, useState } from 'react';

import ErrorState from '../../../commodity/components/ui/ErrorState';
import { formatMoney } from '../../../commodity/format/money';
import {
  acceptEnrollmentRequest,
  declineEnrollmentRequest,
  getTeacherEnrollmentRequests,
  PendingEnrollmentRequest,
  TeacherDeclineReason,
} from '../api/enrollments';
import { DECLINE_REASON_LABELS, formatDeadline, timeLeftLabel } from './enrollmentRequest';

interface Loaded {
  requests: PendingEnrollmentRequest[];
  declineReasons: TeacherDeclineReason[];
  /** Server time at load, and the local instant it arrived — the countdown's anchor. */
  serverNow: number;
  receivedAt: number;
}

/**
 * TCH-13-FR-06 / PRT-07-FR-06 — enrollment requests waiting for this teacher.
 *
 * D-P07-03 (CTO, 12 Oct 2026): the family's card is authorized, not charged.
 * Accepting captures the payment and activates the enrollment; declining
 * needs one of the five D-P07-04 reasons and releases the authorization.
 * The countdown runs from the server's clock, not the device's, so a wrong
 * phone clock cannot show more time than the teacher has.
 */
const PendingEnrollmentRequests: React.FC = () => {
  const [data, setData] = useState<Loaded | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [decliningId, setDecliningId] = useState<string | null>(null);
  const [reason, setReason] = useState<TeacherDeclineReason | ''>('');
  // Re-render each minute so the countdown moves; the value itself is unused.
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await getTeacherEnrollmentRequests();
      setData({
        requests: res.requests,
        declineReasons: res.declineReasons,
        serverNow: Date.parse(res.now),
        receivedAt: Date.now(),
      });
    } catch (err) {
      setLoadError(err);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const now = data ? new Date(data.serverNow + (Date.now() - data.receivedAt)) : new Date();

  const accept = async (r: PendingEnrollmentRequest) => {
    setBusyId(r.enrollmentId);
    setActionError(null);
    setNotice(null);
    try {
      await acceptEnrollmentRequest(r.enrollmentId);
      setNotice(
        `Aceptaste la solicitud${r.childFirstName ? ` de ${r.childFirstName}` : ''}. ` +
          'Se realizó el cobro y la inscripción está activa.',
      );
    } catch (err: any) {
      setActionError(err?.message || 'No se pudo aceptar la solicitud.');
    } finally {
      setBusyId(null);
      await load();
    }
  };

  const decline = async (r: PendingEnrollmentRequest) => {
    if (!reason) return;
    setBusyId(r.enrollmentId);
    setActionError(null);
    setNotice(null);
    try {
      await declineEnrollmentRequest(r.enrollmentId, reason);
      setNotice('Rechazaste la solicitud. A la familia no se le cobró nada.');
      setDecliningId(null);
      setReason('');
    } catch (err: any) {
      setActionError(err?.message || 'No se pudo rechazar la solicitud.');
    } finally {
      setBusyId(null);
      await load();
    }
  };

  if (loadError) {
    return (
      <section style={{ marginBottom: 24 }}>
        <ErrorState
          action="cargar tus solicitudes de inscripción"
          error={loadError}
          onRetry={load}
        />
      </section>
    );
  }
  if (!data) return null;
  if (data.requests.length === 0 && !notice) return null;

  return (
    <section aria-labelledby="pending-requests-heading" style={{ marginBottom: 24 }}>
      <h2 id="pending-requests-heading" style={{ fontSize: 18, marginBottom: 6 }}>
        Solicitudes de inscripción
      </h2>
      <p style={{ color: '#666', fontSize: 13, marginTop: 0 }}>
        La tarjeta de la familia está autorizada, pero no se cobra hasta que aceptes. Si no
        respondes a tiempo, la solicitud vence y no se cobra nada.
      </p>

      {notice && (
        <p role="status" style={{ color: '#2b8a3e', fontSize: 14 }}>
          {notice}
        </p>
      )}
      {actionError && (
        <p role="alert" style={{ color: '#c92a2a', fontSize: 14 }}>
          {actionError}
        </p>
      )}

      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {data.requests.map((r) => {
          const busy = busyId === r.enrollmentId;
          const declining = decliningId === r.enrollmentId;
          const who = r.childFirstName ?? 'Un alumno';
          return (
            <li
              key={r.enrollmentId}
              aria-label={`Solicitud de ${who}`}
              style={{
                border: '1px solid #74c0fc',
                background: '#f4faff',
                borderRadius: 8,
                padding: '12px 14px',
                marginBottom: 8,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <strong>
                  {who} — {r.courseTitle ?? 'tu curso'}
                </strong>
                <span style={{ fontWeight: 600, color: '#1c4f80' }}>
                  {timeLeftLabel(r.acceptBy, now)}
                </span>
              </div>
              <div style={{ fontSize: 13, color: '#495057', marginTop: 4 }}>
                {formatMoney(r.total, r.currency)} autorizados, sin cobrar · Responde antes del{' '}
                {formatDeadline(r.acceptBy)}
              </div>
              {r.captureFailureReason && (
                <p style={{ color: '#c92a2a', fontSize: 13, margin: '6px 0 0' }}>
                  Aceptaste esta solicitud, pero el cobro no se pudo completar. Puedes reintentarlo.
                </p>
              )}

              {declining ? (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                  <label style={{ flex: '1 1 240px', fontSize: 13 }}>
                    Motivo del rechazo
                    <select
                      value={reason}
                      onChange={(e) => setReason(e.target.value as TeacherDeclineReason)}
                      style={{ display: 'block', width: '100%', marginTop: 4 }}
                    >
                      <option value="">Elige un motivo</option>
                      {data.declineReasons.map((code) => (
                        <option key={code} value={code}>
                          {DECLINE_REASON_LABELS[code] ?? code}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    className="btn btn-danger btn-sm"
                    disabled={!reason || busy}
                    onClick={() => decline(r)}
                  >
                    {busy ? 'Rechazando…' : 'Confirmar rechazo'}
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    disabled={busy}
                    onClick={() => {
                      setDecliningId(null);
                      setReason('');
                    }}
                  >
                    Volver
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={busy}
                    onClick={() => accept(r)}
                  >
                    {busy ? 'Aceptando…' : r.captureFailureReason ? 'Reintentar cobro' : 'Aceptar'}
                  </button>
                  {!r.captureFailureReason && (
                    <button
                      className="btn btn-secondary btn-sm"
                      disabled={busy}
                      onClick={() => {
                        setDecliningId(r.enrollmentId);
                        setReason('');
                      }}
                    >
                      Rechazar
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default PendingEnrollmentRequests;
